import type {
  UpdateDesignerPreferencesBody,
  UpdateDesignerPreferencesResponse,
} from "@nlc/shared-types";
import type { UseMutationResult } from "@tanstack/react-query";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";
import authenticatedApi from "@/lib/api/client";

export function useUpdatePreferences(): UseMutationResult<
  UpdateDesignerPreferencesResponse,
  Error,
  UpdateDesignerPreferencesBody
> {
  const projectId = useGetCurrentProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateDesignerPreferencesBody) =>
      await authenticatedApi
        .url(`/projects/${projectId}/designer-preferences`)
        .json(payload)
        .put()
        .json<UpdateDesignerPreferencesResponse>(),
    onError: (error: Error) => {
      const errorMessage =
        (error as { message?: string }).message ||
        "Failed to update preferences";

      toast.add({
        description: errorMessage,
        timeout: 3000,
        title: "Error",
        type: "error",
      });
    },
    onSuccess: () => {
      toast.add({
        description: "Your preferences have been updated successfully",
        timeout: 3000,
        title: "Changes saved",
        type: "success",
      });

      queryClient.invalidateQueries({
        queryKey: ["designer-preferences", projectId],
      });
    },
  });
}
